<main class="page-shell workspace-page">
  <section *ngIf="loading" class="card state">Loading challenge...</section>
  <section *ngIf="error" class="card state status-error">{{ error }}</section>

  <ng-container *ngIf="!loading && !error && problem as p">
    <section class="workspace" [class.editor-fullscreen]="fullscreenEditor">
      <header class="workspace-topbar">
        <div class="topbar-left">
          <span class="topbar-pill" [class.daily]="p.isDaily">
            {{ p.isDaily ? "Daily Question" : "Stream Challenge" }}
          </span>
          <div class="topbar-title">
            <span class="level-tag">#{{ p.levelNumber }}</span>
            <strong>{{ p.title }}</strong>
          </div>
          <span class="difficulty-pill" [class]="p.difficulty.toLowerCase()">{{ p.difficulty }}</span>
          <span class="meta-pill">Acceptance {{ p.acceptanceRate }}%</span>
          <span class="meta-pill">+{{ p.xpReward }} XP</span>
        </div>
        <div class="topbar-right">
          <button class="btn-secondary topbar-btn" [disabled]="runBusy" (click)="runCode()">
            {{ runBusy ? "Running..." : "Run" }}
          </button>
          <button class="btn-primary topbar-btn" [disabled]="submitBusy" (click)="submitCode()">
            {{ submitBusy ? "Submitting..." : "Submit" }}
          </button>
        </div>
      </header>

      <aside class="card left-pane">
        <header class="pane-header">
          <div class="pane-header-main">
            <div class="pane-title">
              <h1>{{ p.title }}</h1>
              <p>World {{ p.worldNumber }} · {{ p.world }} · Level {{ p.worldLevelNumber }}</p>
            </div>
            <div class="pane-stats">
              <span>{{ p.timeLimitMs }} ms</span>
              <span>{{ p.memoryLimitMb }} MB</span>
            </div>
          </div>
          <div class="meta-row">
            <span class="chip">{{ p.topic }}</span>
            <span class="chip" *ngFor="let tag of p.tags.slice(0, 4)">{{ tag }}</span>
          </div>
        </header>

        <nav class="tab-strip">
          <button class="tab" [class.active]="activeProblemTab === 'description'" (click)="selectProblemTab('description')">
            Description
          </button>
          <button class="tab" [class.active]="activeProblemTab === 'editorial'" (click)="selectProblemTab('editorial')">
            Editorial
          </button>
          <button class="tab" [class.active]="activeProblemTab === 'submissions'" (click)="selectProblemTab('submissions')">
            Submissions
          </button>
          <button class="tab" [class.active]="activeProblemTab === 'discussion'" (click)="selectProblemTab('discussion')">
            Discussion
          </button>
        </nav>

        <section class="tab-content" *ngIf="activeProblemTab === 'description'">
          <article>
            <h3>Problem</h3>
            <p>{{ p.description }}</p>
          </article>

          <article>
            <h3>Input Format</h3>
            <p>{{ p.inputFormat || "Use the default method inputs from starter code." }}</p>
          </article>

          <article>
            <h3>Output Format</h3>
            <p>{{ p.outputFormat || "Return the expected result object." }}</p>
          </article>

          <article>
            <h3>Examples</h3>
            <div class="example-list" *ngIf="p.examples.length > 0; else defaultExample">
              <pre class="example code-font" *ngFor="let ex of p.examples">Input: {{ ex.input }}
Output: {{ ex.output }}</pre>
            </div>
            <ng-template #defaultExample>
              <pre class="example code-font">Expected Output: {{ p.expectedOutput }}</pre>
            </ng-template>
          </article>

          <article>
            <h3>Constraints</h3>
            <p>{{ p.constraints || "Favor immutable transformations and avoid side effects." }}</p>
          </article>

          <article class="hint-box">
            <h3>Hints</h3>
            <button class="btn-secondary" (click)="revealHint()" [disabled]="hintLoading || (hintTotalSteps > 0 && hintStep >= hintTotalSteps)">
              {{ hintLoading ? "Loading..." : hintStep === 0 ? "Reveal Hint" : hintStep < hintTotalSteps ? "Next Hint" : "All Hints Unlocked" }}
            </button>
            <p *ngIf="showHint" class="hint">
              <strong *ngIf="hintTotalSteps > 0">Hint {{ hintStep }}/{{ hintTotalSteps }}:</strong>
              {{ hintText }}
            </p>
          </article>
        </section>

        <section class="tab-content" *ngIf="activeProblemTab === 'editorial'">
          <p *ngIf="editorialLoading" class="muted">Loading editorial...</p>
          <p *ngIf="!editorialLoading && editorialError" class="status-error">{{ editorialError }}</p>

          <ng-container *ngIf="!editorialLoading && editorial as e">
            <article>
              <h3>Explanation</h3>
              <pre class="example code-font">{{ e.editorial }}</pre>
            </article>
            <article>
              <h3>Optimized Solution</h3>
              <pre class="example code-font">{{ e.optimizedSolution }}</pre>
            </article>
          </ng-container>
        </section>

        <section class="tab-content" *ngIf="activeProblemTab === 'submissions'">
          <h3>Submission History</h3>
          <p *ngIf="loadingSubmissions" class="muted">Loading submissions...</p>
          <table *ngIf="!loadingSubmissions && recentSubmissions.length > 0" class="submissions-table">
            <thead>
              <tr>
                <th>Status</th>
                <th>Runtime</th>
                <th>Submitted</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let row of recentSubmissions">
                <td [class]="statusClass(row.status)">{{ row.status }}</td>
                <td>{{ row.executionTime ?? "-" }} ms</td>
                <td>{{ row.createdAt | date: "medium" }}</td>
              </tr>
            </tbody>
          </table>
          <p *ngIf="!loadingSubmissions && recentSubmissions.length === 0" class="muted">
            No submissions yet for this problem.
          </p>
        </section>

        <section class="tab-content" *ngIf="activeProblemTab === 'discussion'">
          <h3>Discussion</h3>

          <div class="discussion-compose">
            <textarea
              [(ngModel)]="discussionDraft"
              rows="3"
              placeholder="Share your stream approach, edge-case handling, or optimization ideas..."
            ></textarea>
            <button class="btn-primary" (click)="submitDiscussion()" [disabled]="postingDiscussion">
              {{ postingDiscussion ? "Posting..." : "Post" }}
            </button>
          </div>

          <p *ngIf="discussionError" class="status-error">{{ discussionError }}</p>
          <p *ngIf="discussionLoading" class="muted">Loading discussion...</p>

          <div class="discussion-list" *ngIf="!discussionLoading && discussionPosts.length > 0">
            <article class="discussion-post" *ngFor="let post of discussionPosts">
              <header>
                <div>
                  <strong>{{ post.author?.name || "Anonymous" }}</strong>
                  <span>Lv {{ post.author?.level || 1 }}</span>
                  <span *ngIf="post.parentId" class="reply-pill">Reply</span>
                </div>
                <small>{{ post.createdAt | date: "short" }}</small>
              </header>
              <pre class="discussion-content code-font">{{ post.content }}</pre>
              <button class="vote-btn" (click)="toggleUpvote(post)" [class.active]="post.hasUpvoted">
                Upvote {{ post.upvotes }}
              </button>
            </article>
          </div>

          <p *ngIf="!discussionLoading && discussionPosts.length === 0" class="muted">
            No discussion yet. Be the first to share a solution insight.
          </p>
        </section>
      </aside>

      <section class="card right-pane">
        <header class="editor-toolbar">
          <div class="toolbar-left">
            <span class="toolbar-label">Code</span>
            <span class="chip">Java 17</span>
            <span class="chip">Auto</span>
          </div>
          <div class="toolbar-actions">
            <div class="mode-group">
              <button class="mode-btn" [class.active]="activeMode === 'practice'" (click)="setMode('practice')">
                Practice
              </button>
              <button class="mode-btn" [class.active]="activeMode === 'challenge'" (click)="setMode('challenge')">
                Challenge
              </button>
            </div>
            <button class="btn-secondary" (click)="revealHint()" [disabled]="hintLoading">Hint</button>
            <button class="btn-secondary" (click)="resetTemplate()">Reset</button>
            <button class="btn-secondary" (click)="toggleFullscreenEditor()">
              {{ fullscreenEditor ? "Exit Fullscreen" : "Fullscreen" }}
            </button>
          </div>
        </header>

        <div class="editor-box">
          <ngx-monaco-editor [options]="editorOptions" [(ngModel)]="code"></ngx-monaco-editor>
        </div>

        <section class="console">
          <nav class="tab-strip console-tabs">
            <button class="tab" [class.active]="activeConsoleTab === 'output'" (click)="selectConsoleTab('output')">
              Output
            </button>
            <button class="tab" [class.active]="activeConsoleTab === 'tests'" (click)="selectConsoleTab('tests')">
              Test Cases
            </button>
            <button class="tab" [class.active]="activeConsoleTab === 'result'" (click)="selectConsoleTab('result')">
              Result
            </button>
          </nav>

          <div class="console-body" *ngIf="activeConsoleTab === 'output'">
            <p class="muted">Expected output</p>
            <pre class="code-font">{{ p.expectedOutput }}</pre>

            <label class="input-label">
              Custom test input
              <textarea
                [(ngModel)]="customInput"
                rows="3"
                placeholder="Optional custom input payload for local runs"
              ></textarea>
            </label>
          </div>

          <div class="console-body" *ngIf="activeConsoleTab === 'tests'">
            <p class="muted">Visible sample checks:</p>
            <ul>
              <li *ngFor="let sample of p.sampleTests">{{ sample.message }}</li>
            </ul>
            <p class="muted">Hidden edge-case tests run during submit.</p>
          </div>

          <div class="console-body" *ngIf="activeConsoleTab === 'result'">
            <p *ngIf="!latestResult" class="muted">Run or submit your code to see results.</p>
            <ng-container *ngIf="latestResult as r">
              <p [class]="statusClass(r.status)">Status: {{ r.status }}</p>
              <p>Runtime: {{ r.executionTime ?? "-" }} ms</p>
              <p *ngIf="r.passed !== undefined && r.total !== undefined">Passed: {{ r.passed }} / {{ r.total }}</p>
              <p *ngIf="r.output">Output: <code>{{ r.output }}</code></p>

              <ul *ngIf="r.failedTests.length > 0">
                <li *ngFor="let fail of r.failedTests">{{ fail }}</li>
              </ul>

              <div class="reward-box" *ngIf="r.rewards">
                +{{ r.rewards.gainedXp }} XP · Streak {{ r.rewards.streak }} · Level {{ r.rewards.level }}
              </div>

              <div class="unlock-box" *ngIf="r.unlockedLevel">
                <strong>Next unlocked:</strong>
                Level {{ r.unlockedLevel.levelNumber }} - {{ r.unlockedLevel.title }}
                <button class="btn-primary" (click)="openUnlockedLevel(r.unlockedLevel.id)">Open</button>
              </div>
            </ng-container>
          </div>
        </section>
      </section>
    </section>
  </ng-container>
</main>

